import React, { useState, useRef, useEffect } from 'react';
import { accountLogin, accountLogin2fa, logoutSession, errText } from '../services/api';
import { getQueued, isNetworkError } from '../services/offlineQueue';
import AccountForgotPassword from './AccountForgotPassword';
import { T, S } from '../styles/ds';

const STEP = { CREDENTIALS: 'credentials', TWO_FA: '2fa', TENANT: 'tenant' };

// A request that never reached the server is not a wrong password (post-merge review gate):
// say so, or a connectivity blip sends the owner off to reset a password that was fine.
const NET_ERROR_MSG = 'सर्वर से संपर्क नहीं हो सका — इंटरनेट जाँचें और फिर कोशिश करें / Could not reach the server — check the internet and try again';
// The login succeeded server-side but this device could not store it — a storage problem, never
// "wrong password" (post-merge review gate, HIGH).
const STORE_FAIL_MSG = 'इस डिवाइस पर साइन इन सेव नहीं हो सका (स्टोरेज भरा है या प्राइवेट ब्राउज़िंग) — सामान्य ब्राउज़र विंडो में फिर कोशिश करें / This device could not save the sign-in (storage full or private browsing) — try again in a normal browser window';
// The offline-queue check itself could not be answered (IndexedDB blocked/corrupted) — refuse
// the switch rather than assume empty (pre-push gate round 2, CRITICAL: see QueueUnknownError).
const QUEUE_UNKNOWN_MSG = 'इस डिवाइस की बिक्री जाँची नहीं जा सकी — फिर कोशिश करें, या दुकान के PIN से साइन इन करें / Could not check this device\'s sales — try again, or sign in with the shop PIN instead';
const pendingSalesMsg = (n) =>
  `इस डिवाइस पर ${n} बिक्री अभी सर्वर पर नहीं ${n === 1 ? 'पहुँची है' : 'पहुँची हैं'} — पहले दुकान के PIN से साइन इन करें ताकि वे भेजी जा सकें, फिर अपने खाते से साइन इन करें / This device still has ${n} sale${n === 1 ? '' : 's'} not yet sent to the server — sign in with the shop PIN first so they can finish syncing, then sign in with your account`;

// Thrown by pendingSales() when the offline queue itself could not be read (as opposed to a
// genuinely empty queue) — a distinct case the caller must fail CLOSED on, never silently as "0".
class QueueUnknownError extends Error {}

// How many sales this device still holds — waiting to replay (queued) or stuck (failed).
// Reads getQueued() directly rather than the shared queueCounts() helper, which deliberately
// fails OPEN to zero on an IndexedDB error (post-merge review gate, round 2, CRITICAL): that
// default is correct for queueCounts()'s existing callers — a UI badge should never show a
// scary error just because storage hiccuped — but wrong for THIS decision. An unanswerable
// check is not evidence the queue is empty, and reading it that way here would let a genuinely
// queued sale from the previous shop slip through and be replayed into the one just signed
// into — exactly the bug this guard exists to close. So this fails CLOSED instead: if the queue
// cannot be read, the switch is refused, not silently allowed.
const pendingSales = async () => {
  let all;
  try {
    all = await getQueued();
  } catch (_) {
    throw new QueueUnknownError();
  }
  return (all || []).filter((r) => r.status === 'queued' || r.status === 'failed').length;
};

// Best-effort: by the time any of the failure paths below runs, the backend has ALREADY issued
// the session cookie. If this device cannot finish setting itself up to match, kill the cookie
// rather than leave a live admin session behind a screen that reports failure. POST /auth/logout
// blocklists the token and clears the cookie (routers/auth.py); if even that call fails, the
// cookie simply expires on its own — there is nothing more the client can do (the cookie is
// httpOnly, so no client-side script — including this one — can read or clear it directly).
const abandonSession = async () => {
  try { await logoutSession(); } catch (_) { /* best-effort */ }
};

// Owner sign-in with an Account (email + password), alongside — not instead of — staff PIN login.
// PH-62: the door PH-46 (accounts) and PH-58 (link-to-shop) built but never hung. Mints the SAME
// localStorage session shape loginPin does (App.jsx's bootstrap reads it back after the reload),
// so nothing downstream needs to know which door the owner walked through.
//
// Takes no onSuccess prop (post-merge review gate round 2, GLM/Kimi): a real sign-in ALWAYS goes
// through finish()'s reload path below, since the backend always returns a real tenant_id — the
// in-memory "skip the reload" branch this component had before was already dead code in
// production (round 1's own adversarial review flagged this), and every failure path here ends
// in an error message on this same screen, never a callback. App.jsx no longer passes one.
function AccountSignIn({ onCancel, shopName }) {
  const [step, setStep] = useState(STEP.CREDENTIALS);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [pendingToken, setPendingToken] = useState(null);
  const [tenants, setTenants] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // PH-68 — "Forgot password?" swaps this screen for the request-a-link screen. Held as state here
  // rather than routed, because `email` above stays mounted the whole time: cancelling out of the
  // request screen therefore returns to a credentials step with whatever was already typed still in
  // place, with nothing to copy back and forth.
  const [forgot, setForgot] = useState(false);

  // Ignore a late API response after the owner has already hit "Back to PIN sign-in" (review
  // finding) — without this, an in-flight request that resolves successfully AFTER the user
  // believes they backed out would still silently finish() and sign them in. The setup body
  // must set the ref back to true, not just the cleanup set it false (specialist-verification
  // finding): index.js renders under React.StrictMode, which double-invokes this effect on
  // every mount in development — setup, cleanup, setup again — and a ref that only the cleanup
  // ever touches is left permanently false after that, silently no-op-ing every submit handler
  // on the component's very first real mount.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // A cookie is live the instant accountLogin/accountLogin2fa RETURNS a real tenant_id — that
  // return value is exactly what _issue_account_session already produced server-side, before the
  // client ever sees it. If the owner backs out ("Back to PIN sign-in", which unmounts this
  // screen) in the brief window between that response arriving and this component reacting to
  // it, simply returning here left that cookie fully live and unattended (PR review finding,
  // P1) — with no reload, no localStorage write, and nothing in this tab's own state to show for
  // it. But the cookie itself is real: any subsequent request this browser makes with
  // credentials (including the offline queue's own background sync, which runs on a timer
  // independent of this component) would silently run under it, authenticated as whatever shop
  // the account just signed into, regardless of what the UI or localStorage claim. Call this
  // wherever a `!mountedRef.current` check follows a response that might already carry a cookie.
  const abandonIfUnmounted = async (data) => {
    if (mountedRef.current) return false;
    if (data?.tenant_id) await abandonSession();
    return true;
  };

  // Maps a failure from the two async guards above (pendingSales / the login call itself) to the
  // right message, in priority order: an unreadable queue first (its own distinct explanation),
  // then a request that never reached the server, then the generic credentials fallback.
  const describeError = (err, fallback) =>
    err instanceof QueueUnknownError ? QUEUE_UNKNOWN_MSG
      : isNetworkError(err) ? NET_ERROR_MSG
        : errText(err, fallback);

  // Finish: same localStorage shape the PIN flow writes (App.jsx reads this same key on reload),
  // so an Account-based session survives a refresh exactly like a PIN one.
  //
  // An Account is a GLOBAL identity (unlike PIN login, which is always for THIS device's own
  // resolved tenant) — the multi-shop picker exists exactly so one account can sign into a
  // DIFFERENT shop than whichever this device was last configured for (confirmed against
  // account_auth._issue_account_session, which always returns the real tenant_id). Reusing the
  // cached tenantConfig from a different shop would be dangerous, not just cosmetic —
  // Settings.saveGstInfo() PATCHes the entire cached snapshot (GSTIN, address, UPI VPA,
  // toggles), so an untouched stale field would silently overwrite the newly-authenticated
  // shop's real settings the moment the owner saves anything.
  //
  // This ALWAYS backfills pos_tenant_id and drops the cached tenantConfig before reloading —
  // never conditionally, on a comparison against the device's PRIOR pos_tenant_id. Three review
  // rounds tried variations of that comparison (skip when it matches; skip when it was never
  // set) and each was proven wrong by the next round: pos_tenant_id is only ever set by the
  // invite-URL capture in App.jsx, never by SetupWizard, so a device configured the NORMAL way
  // (direct setup, no invite link — the common case, not an edge one) can carry a perfectly
  // real, populated tenantConfig for shop A while pos_tenant_id sits empty; comparing against
  // that empty value cannot tell "no signal" apart from "definitely still shop A," so it cannot
  // safely skip the reload when the account actually signs into a different shop B. There is no
  // client-side signal that reliably distinguishes "same tenant" from "different tenant" here —
  // so don't try to skip the reload at all. The one-time reload happens only on this pre-login
  // screen (no in-progress cart/sale state exists yet), mirrors App.jsx's own invite-URL
  // tenant-switch mechanism (drop the stale cache, point the device at the new slug), and
  // guarantees every cached page state — not just this component's — starts over clean for
  // whichever shop was actually just authenticated into.
  //
  // Write order matters (post-merge review gate, HIGH): cached config first, slug second,
  // session LAST — of the two possible orderings, this is the one where a failure part-way
  // through never leaves an active session pointed at the wrong tenant's data. The alternative
  // order (session before slug) risks a live session already claiming the new shop's role while
  // pos_tenant_id — and therefore every API call's tenant scoping — still names the old one. On
  // any failure the two keys below are rolled back to what they held before this attempt (PR
  // review finding, P2 — a real gap: this comment used to claim a failure here "reloads to that
  // shop's own PIN screen," but the code never actually reloaded on that path, so whichever
  // partial write had already landed stayed in storage while the screen kept showing the
  // PREVIOUS shop — a mismatch a PIN typed after backing out would be silently checked against).
  // All three writes share ONE try so a storage failure is reported as a storage problem, never
  // as a wrong password: the login already succeeded server-side by this point.
  // finish() only ever runs after the backend has already accepted the credentials (or 2FA
  // code) — that is what produced `data` in the first place — so nothing that fails inside it
  // is ever a wrong password. None of the branches below clear it (round-5 review finding):
  // unlike handleCredentialsSubmit's own catch, which does need to tell an actual rejection
  // apart from a device-side problem, everything reaching finish() has already cleared that bar.
  const finish = async (data) => {
    // Fail closed on a session with no tenant_id: the backend always sends one, so its absence
    // is a response shape this screen does not understand — never guess which shop it is for.
    if (!data?.tenant_id) {
      await abandonSession();
      if (!mountedRef.current) return;
      setError('साइन इन नहीं हो सका / Could not sign in — try again');
      return;
    }
    // Defensive re-run of the pending-sales guard (the primary check runs BEFORE any request in
    // handleCredentialsSubmit — see there for why). By now the cookie is live, so a non-empty
    // (or unreadable) queue here means the reload below would risk replaying another shop's
    // sales into this one.
    let pending;
    try {
      pending = await pendingSales();
    } catch (err) {
      await abandonSession();
      if (!mountedRef.current) return;
      setError(describeError(err));
      return;
    }
    // A real tenant_id was already confirmed above, so the cookie is unconditionally live here —
    // unlike the handler-level checks that use abandonIfUnmounted (which still have to ask
    // whether THIS response carried one), this one never needs to ask.
    if (!mountedRef.current) {
      await abandonSession();
      return;
    }
    if (pending > 0) {
      await abandonSession();
      if (!mountedRef.current) return;
      setError(pendingSalesMsg(pending));
      return;
    }
    // Capture what was there BEFORE any write, so a failure part-way through can be undone (PR
    // review finding, P2): the ORIGINAL comment above claimed a failure here "reloads to that
    // shop's own PIN screen," but the code never actually reloaded on this path — it stayed on
    // THIS screen, still describing the previous shop, while pos_tenant_id (if that write had
    // already landed) now named the NEW one. Anyone using the still-rendered PIN screen after
    // backing out would then have their PIN checked against the wrong shop. Restoring these two
    // keys on failure keeps whatever is on screen in sync with what is actually in storage.
    const prevTenantConfig = localStorage.getItem('tenantConfig');
    const prevPosTenantId = localStorage.getItem('pos_tenant_id');
    const prevSession = localStorage.getItem('session');
    try {
      localStorage.removeItem('tenantConfig');
      localStorage.setItem('session', JSON.stringify({
        role: data.role, warehouse_id: data.warehouse_id, warehouse_name: data.warehouse_name,
        expiry: Date.now() + 8 * 60 * 60 * 1000,
      }));
      // PH-50 — the shop key goes LAST. Every other window on this device stops itself the moment
      // this key names a different shop (App.jsx), so it must only ever change once everything
      // else has been written; a storage failure above then never shows the other windows a
      // switch that is about to be rolled back (review finding, PH-50 round 3).
      localStorage.setItem('pos_tenant_id', data.tenant_id);
    } catch (_) {
      try {
        if (prevSession !== null) localStorage.setItem('session', prevSession);
        else localStorage.removeItem('session');
        if (prevTenantConfig !== null) localStorage.setItem('tenantConfig', prevTenantConfig);
        else localStorage.removeItem('tenantConfig');
        if (prevPosTenantId !== null) localStorage.setItem('pos_tenant_id', prevPosTenantId);
        else localStorage.removeItem('pos_tenant_id');
      } catch (_) { /* the device is already out of storage — nothing more to undo client-side */ }
      await abandonSession();
      if (!mountedRef.current) return;
      setError(STORE_FAIL_MSG);
      return;
    }
    window.location.reload();
  };

  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      // Post-merge review gate, CRITICAL: a successful account sign-in re-points this device at
      // whichever shop the account signed into and reloads; App.jsx's mount effect then replays
      // every sale still sitting in this device's offline queue under the NEW session's cookie.
      // The queue records carry no shop tag (offlineQueue.enqueueSale) and the backend attributes
      // a replayed sale to the session's tenant (stock_out.process_sale) — so a device holding
      // shop A's unsent sales would post them into shop B's stock and sales ledger. No account
      // sign-in while anything is still waiting — or while that can't even be checked (see
      // pendingSales) — the shop-PIN path syncs them under the right shop first. Checked BEFORE
      // this credentials request, so this path never issues a cookie for a switch that must not
      // happen. handleCodeSubmit and handleTenantSelect run the identical check before their own
      // requests too (round-4 review finding: real user think-time — typing a TOTP code, reading
      // a shop picker — made that a genuinely reachable window, not the sub-millisecond one
      // between finish()'s own re-check and the reload). finish() itself re-checks once more
      // regardless of which of the three paths got there, since a cookie can only be abandoned
      // once it already exists.
      const pending = await pendingSales();
      if (!mountedRef.current) return;
      if (pending > 0) {
        setError(pendingSalesMsg(pending));
        return;   // nothing was sent anywhere — the typed credentials can stay for a retry
      }
      const res = await accountLogin(email.trim(), password);
      // "Back to PIN sign-in" was clicked mid-request — abandonIfUnmounted() also tears down the
      // cookie if res.data already carries a real tenant_id (PR review finding, P1): the check
      // used to just return here, leaving a genuinely live cookie behind with nothing done about
      // it whenever this exact response happened to be the terminal-success shape.
      if (await abandonIfUnmounted(res.data)) return;
      if (res.data.requires_2fa) {
        setPendingToken(res.data.pending_token);
        setCode('');
        setStep(STEP.TWO_FA);
      } else if (res.data.needs_tenant) {
        setTenants(res.data.tenants);
        setStep(STEP.TENANT);
      } else {
        await finish(res.data);
      }
    } catch (err) {
      if (!mountedRef.current) return;
      setError(describeError(err, 'ईमेल या पासवर्ड गलत है / Incorrect email or password'));
      // Only a real credentials rejection should cost the typed password (round-3 review
      // finding): a queue-check failure or a request that never reached the server is a device
      // or connectivity problem, not proof the password was wrong, and the 'pending > 0' branch
      // above already keeps it for exactly that reason — this catch was the one place that
      // didn't match.
      if (!(err instanceof QueueUnknownError) && !isNetworkError(err)) setPassword('');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    // Same pre-request guard as handleCredentialsSubmit, run as its own try/catch so a
    // queue-side failure never disturbs the in-progress 2FA attempt below — this is a device or
    // connectivity problem, not a wrong code, so the typed pending_token/code are left alone and
    // the owner can simply retry once it clears (round-4 review finding: typing a TOTP code
    // takes real time, unlike the sub-millisecond window between finish()'s own re-check and the
    // reload that this repo's review gate kept re-flagging on unchanged code).
    try {
      const pending = await pendingSales();
      if (!mountedRef.current) return;
      if (pending > 0) {
        setError(pendingSalesMsg(pending));
        if (mountedRef.current) setLoading(false);
        return;
      }
    } catch (err) {
      if (!mountedRef.current) return;
      setError(describeError(err));
      if (mountedRef.current) setLoading(false);
      return;
    }
    try {
      const res = await accountLogin2fa(pendingToken, code.trim());
      // Same as handleCredentialsSubmit's own call above (PR review finding, P1): this response
      // may already have set a real cookie, and unmounting here must not leave it unattended.
      if (await abandonIfUnmounted(res.data)) return;
      if (res.data.needs_tenant) {
        setTenants(res.data.tenants);
        setStep(STEP.TENANT);
      } else {
        await finish(res.data);
      }
    } catch (err) {
      if (!mountedRef.current) return;
      // The backend invalidates pending_token on EITHER outcome of a code check, wrong guesses
      // included (account_auth.login_2fa: "single-use on either outcome") — so retyping even the
      // CORRECT code against this same token would now fail with a confusing "already used"
      // error (review finding). Reset all the way back to credentials, same as
      // handleTenantSelect's failure path, so the next submit mints a fresh pending_token.
      // Only override the message for the actual "wrong code" case — that backend detail,
      // shown verbatim, would land on the credentials screen next to email/password and read as
      // if the PASSWORD was wrong (specialist-verification finding). Anything else reaching this
      // catch (IP throttle, an already-expired pending_token) has its own real, actionable
      // backend message (round-2 review finding: overriding unconditionally silently discarded
      // the throttle wait-time message) — show that instead of a wrong diagnosis. A request that
      // never got a response is named as such (post-merge review gate).
      const detail = err?.response?.data?.detail;
      setError(isNetworkError(err)
        ? NET_ERROR_MSG
        : detail === 'Incorrect code'
          ? 'गलत कोड — दोबारा साइन इन करें / Incorrect code — sign in again'
          : errText(err, 'गलत कोड — दोबारा साइन इन करें / Incorrect code — sign in again'));
      setStep(STEP.CREDENTIALS);
      setPendingToken(null);
      setCode('');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  const handleTenantSelect = async (tenantId) => {
    setLoading(true);
    setError('');
    // Same pre-request guard as handleCredentialsSubmit, and for the same reason (round-4 review
    // finding): reading a shop picker and tapping the right one takes real time. Its own try, so
    // a queue-side failure doesn't fall into the "could not sign in, reset to credentials" branch
    // below — the picker/pending_token stay intact, since this is a device problem, not a
    // rejected sign-in.
    try {
      const pending = await pendingSales();
      if (!mountedRef.current) return;
      if (pending > 0) {
        setError(pendingSalesMsg(pending));
        if (mountedRef.current) setLoading(false);
        return;
      }
    } catch (err) {
      if (!mountedRef.current) return;
      setError(describeError(err));
      if (mountedRef.current) setLoading(false);
      return;
    }
    try {
      // A 2FA login's pending_token stays valid until a session actually issues (see
      // account_auth.login_2fa) — reuse it here rather than restarting the whole flow.
      const res = pendingToken
        ? await accountLogin2fa(pendingToken, code.trim(), tenantId)
        : await accountLogin(email.trim(), password, tenantId);
      // Same as the other two submit handlers (PR review finding, P1): a shop pick always
      // carries a real tenant_id on success, so unmounting here must not leave that cookie
      // unattended either.
      if (await abandonIfUnmounted(res.data)) return;
      await finish(res.data);
    } catch (err) {
      if (!mountedRef.current) return;
      setError(describeError(err, 'साइन इन नहीं हो सका / Could not sign in — try again'));
      setTenants(null);
      setStep(STEP.CREDENTIALS);
      // Drop everything from the failed attempt (review finding) — a retry must start clean,
      // never resubmit a pending_token/code pair from the prior attempt, and never leave the
      // email sitting on screen for whoever is next to look at this device after the shop
      // picker showed which shops it belongs to.
      setPassword('');
      setPendingToken(null);
      setCode('');
      setEmail('');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  // Design-system tokens only (Codex PR review) — no local style constants. S.cardPad/S.input/
  // S.btnPri come from styles/ds.js; the two pixel values below (page wrapper height/centering,
  // the 2FA code's larger digit display) aren't tokens anywhere else in the app either, so they
  // stay as plain layout, same as Login.jsx.
  const wrapStyle = { minHeight: '100vh', background: T.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: T.font };
  const btnDisabled = { opacity: 0.5, cursor: 'default' };

  // PH-68 — the request-a-link screen replaces this one while it is open. This component stays
  // mounted, so `email` survives and cancelling returns to the credentials step with it intact.
  if (forgot) {
    return <AccountForgotPassword initialEmail={email} onCancel={() => setForgot(false)} />;
  }

  return (
    <div style={wrapStyle}>
      <div style={{ marginBottom: 32, textAlign: 'center' }}>
        <div style={{ fontSize: 30, fontWeight: 800, color: T.ink, letterSpacing: -0.5 }}>{shopName || 'हमारी दुकान'}</div>
        <div style={{ fontSize: 13, color: T.sec, marginTop: 4 }}>अपने खाते से साइन इन करें — Sign in with your account</div>
      </div>

      <div style={{ ...S.cardPad, width: '100%', maxWidth: 360 }}>
        {step === STEP.CREDENTIALS && (
          <form onSubmit={handleCredentialsSubmit}>
            <label htmlFor="account-email" style={S.label}>ईमेल / Email</label>
            <input
              id="account-email" type="email" autoComplete="email" autoFocus required
              value={email} onChange={e => { setEmail(e.target.value); setError(''); }}
              placeholder="you@example.com" style={{ ...S.input, marginBottom: 12 }}
            />
            <label htmlFor="account-password" style={S.label}>पासवर्ड / Password</label>
            <input
              id="account-password" type="password" autoComplete="current-password" required
              value={password} onChange={e => { setPassword(e.target.value); setError(''); }}
              placeholder="••••••••" style={{ ...S.input, marginBottom: 12 }}
            />
            {error && <div role="alert" style={{ ...S.alertDetail, textAlign: 'center' }}>{error}</div>}
            <button type="submit" disabled={!email || !password || loading}
              style={{ ...S.btnPri, width: '100%', ...((!email || !password || loading) ? btnDisabled : {}) }}>
              {loading ? 'जाँच हो रही है...' : 'साइन इन करें / Sign in →'}
            </button>
            {/* PH-68 — a real <button>, so it is keyboard-operable and never submits this form.
                type="button" matters: inside a <form>, a button with no type defaults to submit,
                which would fire a sign-in attempt with the password field as it stands.
                Codex review finding on PR #68 (real, confirmed): disabled while `loading` —
                swapping to the forgot-password form does not unmount AccountSignIn, so
                `mountedRef` stays true and a late-arriving successful /account/login response
                would still call finish() and sign the owner in while they are looking at this
                form. */}
            <button
              type="button" disabled={loading} onClick={() => { setError(''); setForgot(true); }}
              style={{ ...S.btnSec, width: '100%', marginTop: 10, fontSize: 12, ...(loading ? btnDisabled : {}) }}
            >
              पासवर्ड भूल गए? / Forgot password?
            </button>
          </form>
        )}

        {step === STEP.TWO_FA && (
          <form onSubmit={handleCodeSubmit}>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: T.ink }}>प्रमाणीकरण कोड डालें</div>
              <div style={{ fontSize: 12, color: T.mute, marginTop: 2 }}>
                अपने authenticator app में दिख रहा कोड डालें / Enter the code from your authenticator app
              </div>
            </div>
            <input
              type="text" inputMode="numeric" maxLength={10} autoFocus autoComplete="one-time-code"
              value={code} onChange={e => { setCode(e.target.value); setError(''); }}
              placeholder="000000"
              style={{ ...S.input, marginBottom: 12, textAlign: 'center', fontSize: 22, letterSpacing: 8 }}
            />
            {error && <div role="alert" style={{ ...S.alertDetail, textAlign: 'center' }}>{error}</div>}
            <button type="submit" disabled={code.trim().length < 6 || loading}
              style={{ ...S.btnPri, width: '100%', ...((code.trim().length < 6 || loading) ? btnDisabled : {}) }}>
              {loading ? 'जाँच हो रही है...' : 'पुष्टि करें / Verify →'}
            </button>
          </form>
        )}

        {step === STEP.TENANT && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: T.ink }}>दुकान चुनें</div>
              <div style={{ fontSize: 12, color: T.mute, marginTop: 2 }}>
                यह खाता एक से ज़्यादा दुकानों से जुड़ा है — एक चुनें / This account is linked to more than
                one shop — select one
              </div>
            </div>
            {error && <div role="alert" style={{ ...S.alertDetail, textAlign: 'center' }}>{error}</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(tenants || []).map(t => (
                <button
                  key={t.tenant_id} type="button" disabled={loading} onClick={() => handleTenantSelect(t.tenant_id)}
                  style={{
                    ...S.btnSec, width: '100%', textAlign: 'left', justifyContent: 'flex-start',
                    background: T.bg2, fontWeight: 600, color: T.ink, fontSize: 15, padding: '13px 16px',
                    ...(loading ? btnDisabled : {}),
                  }}
                >
                  🏪 {t.shop_name}
                </button>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
          <button type="button" onClick={onCancel}
            style={{ background: 'none', border: 'none', color: T.mute, fontSize: 12, cursor: 'pointer' }}>
            ← PIN से साइन इन करें / Back to PIN sign-in
          </button>
        </div>
      </div>
    </div>
  );
}

export default AccountSignIn;
